import React, { useState } from "react";
import Modal from "react-modal";
import { IoMdCloseCircleOutline } from "react-icons/io";
import { Bounce, toast } from "react-toastify";
import { doc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../../firebase";
import {
  COLLECTION_NAME,
  FIREBASE_USER_DELETE,
  SEND_EMAIL,
} from "../../constants/endpoints";
import { Collapse } from "antd";

Modal.setAppElement("#root");

const VerificationModal = ({ isOpen, onRequestClose, user, onVerify }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [approveLoading, setApproveLoading] = useState(false);
  const [declineLoading, setDeclineLoading] = useState(false);

  if (!user) return null;

  const isDriver = user.role === "Rider";
  const isPassenger = user.role === "passenger";

  const handleVerify = async () => {
    setApproveLoading(true);
    try {
      const userRef = doc(db, COLLECTION_NAME.users, user.id);
      await updateDoc(userRef, { documentStatus: "approved" });

      const emailResponse = await fetch(SEND_EMAIL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: "welcome",
          email: user.email?.trim(),
        }),
      });

      if (!emailResponse.ok) {
        toast.error("Could not send verification email to the user.", {
          position: "top-right",
          autoClose: 5000,
          transition: Bounce,
        });
        return;
      }

      toast.success("User documents verified and email notification sent!", {
        position: "top-right",
        autoClose: 5000,
        transition: Bounce,
      });

      onVerify(user.id);
      onRequestClose();
    } catch (error) {
      console.error("Error verifying document: ", error);
    } finally {
      setApproveLoading(false);
    }
  };

  const handleDecline = async () => {
    setDeclineLoading(true);
    try {
      const userRef = doc(db, COLLECTION_NAME.users, user.id);

      const emailResponse = await fetch(SEND_EMAIL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          type: "rejection",
          email: user.email?.trim(),
        }),
      });

      if (!emailResponse.ok) {
        toast.error("Failed to send rejection email to the user.", {
          position: "top-right",
          autoClose: 5000,
          transition: Bounce,
        });
        setDeclineLoading(false);
        return;
      }

      await updateDoc(userRef, { documentStatus: "declined", email: "" });
      //Removing hard delete for now
      // await deleteDoc(userRef);
      const deleteResponse = await fetch(
        FIREBASE_USER_DELETE.replace(":id", user.id),
        {
          method: "DELETE",
        }
      );

      const result = await deleteResponse.json();

      if (!result.success) {
        toast.error("Failed to delete user from Firebase Auth.", {
          position: "top-right",
          autoClose: 5000,
          transition: Bounce,
        });
      }

      toast.success(
        "User has been declined, email sent, and removed successfully.",
        {
          position: "top-right",
          autoClose: 5000,
          transition: Bounce,
        }
      );

      onVerify(user.id);
      onRequestClose();
    } catch (error) {
      console.error("Error declining document: ", error);
      toast.error("Error declining document. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        transition: Bounce,
      });
    } finally {
      setDeclineLoading(false);
    }
  };

  const UserDetailItem = ({ label, value, type }) => (
    <div className="flex items-center justify-between py-2 px-4 border-b border-gray-200">
      <strong className="text-gray-700 font-semibold">{label}:</strong>
      {type === "text" ? (
        <span className="text-gray-500">{value}</span>
      ) : (
        <img
          src={value}
          alt={label}
          className="h-10 w-10 rounded-full object-cover cursor-pointer"
          onClick={() => setSelectedImage(value)}
        />
      )}
    </div>
  );

  const userDetails = [
    { label: "Name", value: user.fullName, type: "text" },
    { label: "Email", value: user.email, type: "text" },
    { label: "Phone", value: user.phoneNumber, type: "text" },
    {
      label: "Role",
      value: ["Rider", "Driver"].includes(user.role) ? "Driver" : user.role,
      type: "text",
    },
    { label: "Rating", value: user.rating, type: "text" },
    { label: "Document Status", value: user.documentStatus, type: "text" },
  ];

  // Add ID card picture for passengers
  if (isPassenger) {
    userDetails.push({
      label: "ID Card",
      value: user?.idCardPicture,
      type: "image",
    });
  }

  const getCarDetails = (vehicle) => [
    { label: "Car Make", value: vehicle?.carMake, type: "text" },
    { label: "Car Model", value: vehicle?.carModal, type: "text" },
    { label: "Car Color", value: vehicle?.carColor, type: "text" },
    { label: "Car Plate", value: vehicle?.carPlate, type: "text" },
    { label: "Vehicle Type", value: vehicle?.vehicleType, type: "text" },
    {
      label: "Car Insurance Info",
      value: vehicle?.carInsuranceInfo,
      type: "image",
    },
    { label: "Taxi License", value: vehicle?.taxiLicense, type: "image" },
    {
      label: "Driver License",
      value: vehicle?.driverLicense,
      type: "image",
    },
  ];

  let carDetailsArray = [];

  if (isDriver && user.vehicles && user.vehicles.length > 0) {
    carDetailsArray = user.vehicles.map(getCarDetails);
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="User Details"
      className="bg-white rounded-xl shadow-xl p-6 max-w-3xl mt-16 mx-auto max-h-[80%] overflow-y-auto custom-scrollbar relative"
      overlayClassName="fixed inset-0 bg-black bg-opacity-70"
    >
      <button
        onClick={onRequestClose}
        className="absolute top-3 right-3 text-red-500 hover:text-red-600 text-2xl"
      >
        <IoMdCloseCircleOutline size={26} />
      </button>

      <div className="flex flex-col items-center mb-6">
        <img
          src={user.profilePicture}
          alt="Profile"
          className="w-32 h-32 rounded-full shadow-lg mb-4 cursor-pointer"
          onClick={() => setSelectedImage(user.profilePicture)}
        />
        <h2 className="text-dazzledBlue text-2xl font-bold">
          {isDriver ? "Driver" : "Passenger"} Details
        </h2>
      </div>

      <div className="space-y-4">
        <div className="bg-gray-100 rounded-lg shadow-lg p-4">
          <h3 className="text-xl font-semibold text-dazzledBlue mb-3">
            Personal Details
          </h3>
          {userDetails.map(({ label, value, type }, index) => (
            <UserDetailItem
              key={index}
              label={label}
              value={value}
              type={type}
            />
          ))}
        </div>

        {isDriver && carDetailsArray.length > 0 && (
          <div className="bg-gray-100 rounded-lg shadow-lg p-4">
            <h3 className="text-xl font-semibold text-dazzledBlue mb-3">
              Car Details
            </h3>
            <Collapse accordion defaultActiveKey={["0"]}>
              {carDetailsArray.map((carDetails, idx) => (
                <Collapse.Panel
                  header={
                    <span className="font-bold">{`Vehicle #${idx + 1}`}</span>
                  }
                  key={idx.toString()}
                >
                  {carDetails.map(({ label, value, type }, index) => (
                    <UserDetailItem
                      key={index}
                      label={label}
                      value={value}
                      type={type}
                    />
                  ))}
                </Collapse.Panel>
              ))}
            </Collapse>
          </div>
        )}
      </div>

      <div className="flex justify-center gap-4 mt-6">
        <button
          onClick={handleDecline}
          disabled={declineLoading || approveLoading}
          className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-10 rounded-md shadow-md flex items-center"
        >
          {declineLoading ? (
            <div className="w-6 h-6 border-4 border-t-white border-red-700 rounded-full animate-spin mr-2"></div>
          ) : (
            "Decline"
          )}
        </button>
        <button
          onClick={handleVerify}
          disabled={approveLoading || declineLoading}
          className="text-primary bg-celadon font-semibold py-2 px-10 rounded-md shadow-md flex items-center"
        >
          {approveLoading ? (
            <div className="w-6 h-6 border-4 border-t-white border-[#0D3D51] rounded-full animate-spin mr-2"></div>
          ) : (
            "Approve"
          )}
        </button>
      </div>

      {selectedImage && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 z-50">
          <div className="relative">
            <img
              src={selectedImage}
              alt="Full View"
              className="max-h-[70vh] rounded-lg shadow-lg"
            />
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-1 right-3 text-3xl text-red-700"
            >
              &times;
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default VerificationModal;

